import React, { Fragment, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import ReactTooltip from 'react-tooltip';

import { CapEditTextFieldDefaultProps, CapEditTextFieldPropTypes } from 'src/components/CapField/propTypes';
import s from 'src/components/CapField/CapEditField.styles';
import sf from 'src/components/CapFinding/CapFinding.styles';
import { removeInvalidXmlChars } from 'src/utils/helpers';

const CapEditTextField = ({
  action,
  editable,
  forTitle,
  isRequired,
  isTooltipDisabled,
  onSave,
  reviewNotes,
  type,
  value,
}) => {
  const { t } = useTranslation();

  const [editing, setEditing] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const inputRef = useRef(null);
  const isTextarea = type === 'textarea';
  const TextFieldComponent = isTextarea ? s.TextAreaField : s.TextField;

  const onClickSave = async () => {
    await onSave(inputRef.current.value);
    setEditing(false);
  };

  const onClickEdit = async () => {
    if (!editable) {
      return;
    }
    await setEditing(true);
    setInputValue(value);
    inputRef.current.focus();

    const tmpStr = inputRef.current.value;
    inputRef.current.value = '';
    inputRef.current.value = tmpStr;
  };

  const onClickEmptyEdit = async () => {
    if (!editable) {
      return;
    }
    await setEditing(true);

    inputRef.current.focus();
  };

  const divFunction = () => {
    if (!editing && value && reviewNotes) {
      return (
        <s.ReviewNotesBox>
          <s.EditDiv
            editable={editable}
            onClick={onClickEmptyEdit}
            vertical-center
          >
            <sf.Text>{value}</sf.Text>
          </s.EditDiv>
          <s.ReviewNotesInfo>
            <s.ReviewedDate>
              {moment(action.reviewedDate).format('MMM DD,YYYY')}
            </s.ReviewedDate>
            <s.ReviewedBy>
              {`${action.reviewedByUser.lastName} ${action.reviewedByUser.firstName}`}
            </s.ReviewedBy>
          </s.ReviewNotesInfo>
        </s.ReviewNotesBox>
      );
    }
    if (!editing && !value && isRequired) {
      return (
        <s.EditDiv
          editable={editable}
          isRequired
          onClick={onClickEmptyEdit}
          vertical-center
          >
          <s.PlaceHolderText>{t('REQUIRED')}</s.PlaceHolderText>
        </s.EditDiv>
      );
    }

    if (!editing && !value) {
      return (
        <s.EditDiv
          editable={editable}
          onClick={onClickEmptyEdit}
          vertical-center
          >
          <s.PlaceHolderText>{t('EMPTY')}</s.PlaceHolderText>
        </s.EditDiv>
      );
    }
    if (!editing && value) {
      return (
        <Fragment>
          { isTooltipDisabled ? null : ( <ReactTooltip id="CapEditTextField" place="right" /> ) }
          <s.EditDiv
            border
            data-tip={isTooltipDisabled ? null : value}
            editable={editable}
            for-cap-text={true}
            onClick={onClickEdit}
            oneLine={true}
            vertical-center
          >
            <sf.Text
              centerAligned={!isTextarea}
              one-line={!isTextarea}
              title={forTitle}
            >
              {value}
            </sf.Text>
          </s.EditDiv>
        </Fragment>
      );
    }

    return null;
  };

  return (
    <Fragment>
      {divFunction()}

      <div style={{ display: editing ? null : 'none', }}>
        <s.Row>
          <TextFieldComponent
            forTitle={forTitle}
            onChange={e => setInputValue(removeInvalidXmlChars(e.target.value))}
            ref={inputRef}
            type="text"
            value={inputValue}
          />
        </s.Row>
        <s.Row paddingForButtons>
          <s.Button onClick={onClickSave} orange sm solid>{t('SAVE')}</s.Button>
          <s.Button ghost onClick={() => setEditing(false)} red sm>
            {t('CANCEL')}
          </s.Button>
        </s.Row>
      </div>
    </Fragment>
  );
};

CapEditTextField.propTypes = CapEditTextFieldPropTypes;
CapEditTextField.defaultProps = CapEditTextFieldDefaultProps;

export default React.memo(CapEditTextField);
